import { useState } from 'react';
import { Upload, Download, AlertCircle, FileSpreadsheet, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { productsAPI } from '../../services/api';
import { toast } from 'sonner';

/*
 * Bulk Product Upload — maps the BlueSpark V4.0 product CSV structure to API fields.
 *
 * Accepted CSV header columns (matches Excel column letters in brackets):
 *  sku (A)                         -> id (key)
 *  product_title (B)               -> name
 *  product_url (C)                 -> productUrl
 *  price (D)                       -> price (numeric)
 *  currency (E)                    -> currency (default AUD)
 *  manufacturer (F)                -> make
 *  model (G)                       -> model
 *  cc (H)                          -> cc
 *  stock_power (I)                 -> stockPower
 *  tuned_power (J)                 -> auto-computes gainPower = tuned - stock
 *  stock_torque (K)                -> stockTorque
 *  tuned_torque (L)                -> auto-computes gainTorque = tuned - stock
 *  fuel_system (M)                 -> fuelSystem
 *  connections (N)                 -> connections
 *  engine_id (O)                   -> engineId
 *  instructions_id (P)             -> instructionsId
 *  instructions_url (Q)            -> instructionsUrl
 *  unbranded_instructions_url (R)  -> unbrandedInstructionsUrl
 *  rail_map (S)                    -> railMap
 *  boost (T)                       -> boost
 *  cam (U)                         -> cam
 *  available (V)                   -> inStock (Yes/No/true/false)
 *  category (W)                    -> category slug
 *  description (X)                 -> description
 *  image (Y)                       -> image URL (carousel slide 1)
 *  additional_image_1 (Z)          -> detailImages[0]
 *  additional_image_2 (AA)         -> detailImages[1]
 *  additional_image_3 (AB)         -> detailImages[2]
 *  additional_image_4 (AC)         -> detailImages[3]
 *  installation_video_url (AD)     -> installationVideoUrl (YouTube/Vimeo)
 *  bluetooth_enabled (AE)          -> bluetoothEnabled (Yes/No)
 */

const CSV_HEADERS_TO_API = {
  sku: 'sku',
  product_title: 'name',
  product_url: 'productUrl',
  price: 'price',
  currency: 'currency',
  manufacturer: 'make',
  make: 'make',
  model: 'model',
  cc: 'cc',
  engine_cc: 'cc',
  stock_power: 'stockPower',
  tuned_power: 'tunedPower',
  stock_torque: 'stockTorque',
  tuned_torque: 'tunedTorque',
  gain_power: 'gainPower',
  gain_torque: 'gainTorque',
  fuel_system: 'fuelSystem',
  connections: 'connections',
  engine_id: 'engineId',
  instructions_id: 'instructionsId',
  instructions_url: 'instructionsUrl',
  unbranded_instructions_url: 'unbrandedInstructionsUrl',
  rail_map: 'railMap',
  boost: 'boost',
  cam: 'cam',
  available: 'inStock',
  in_stock: 'inStock',
  category: 'category',
  description: 'description',
  image: 'image',
  // V4.0 additions — backend collapses the 4 image columns into detailImages[].
  additional_image_1: 'additional_image_1',
  additional_image_2: 'additional_image_2',
  additional_image_3: 'additional_image_3',
  additional_image_4: 'additional_image_4',
  installation_video_url: 'installationVideoUrl',
  bluetooth_enabled: 'bluetoothEnabled',
};

const TEMPLATE_HEADERS = [
  'sku', 'product_title', 'product_url', 'price', 'currency',
  'manufacturer', 'model', 'cc',
  'stock_power', 'tuned_power', 'stock_torque', 'tuned_torque',
  'fuel_system', 'connections', 'engine_id', 'instructions_id',
  'instructions_url', 'unbranded_instructions_url',
  'rail_map', 'boost', 'cam', 'available',
  'category', 'description', 'image',
  'additional_image_1', 'additional_image_2', 'additional_image_3', 'additional_image_4',
  'installation_video_url', 'bluetooth_enabled',
];

const SAMPLE_ROWS = [
  // 1. Update existing product price + images + Bluetooth flag
  ['p-6597', 'BMW 518d 143PS 2.0 - Bluespark Pro Tuning Module', 'bmw-518d-143-ps-pro', '396.00', 'AUD',
   'BMW', '518d', '1995',
   '143', '178', '360', '430',
   'Common Rail', 'Rail Only', 'N47S1', '009',
   'https://bluesparkautomotive.com/instructionfiles/009.pdf',
   'https://bluesparkautomotive.com/instructionfiles/009x.pdf',
   'K3drail', 'H3a', '0', 'Yes',
   'pro-diesel', 'BMW 518d Pro Diesel Tuning Module',
   'https://bluesparkautomotive.com/media/catalog/product/p/-/p-1-min.png',
   'https://bluesparkautomotive.com/media/catalog/product/p/-/p-2-min.png',
   'https://bluesparkautomotive.com/media/catalog/product/p/-/p-3-min.png',
   'https://bluesparkautomotive.com/media/catalog/product/b/l/bluetooth_resized.png',
   '',
   'https://www.youtube.com/watch?v=IE32WRIlRuw', 'Yes'],
  // 2. Add a brand-new vehicle (no SKU = auto-generated)
  ['', 'BMW M3 G80 480PS 3.0 - Bluespark Pro Petrol', 'bmw-m3-g80-480-ps', '594.00', 'AUD',
   'BMW', 'M3', '2993',
   '480', '535', '550', '640',
   'Direct Injection', 'Rail Only', 'S58', '',
   '', '',
   '', '', '', 'Yes',
   'pro-petrol', 'Bluespark Pro Petrol module for BMW M3 G80',
   'https://bluesparkautomotive.com/media/catalog/product/pt/pt-1-min.png',
   'https://bluesparkautomotive.com/media/catalog/product/pt/pt-2-min.png',
   '', '', '',
   '', 'Yes'],
];

// Robust CSV parser supporting quoted fields with commas
const parseCsv = (text) => {
  const rows = [];
  let cur = '';
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
      } else {
        cur += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(cur); cur = ''; }
      else if (c === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; }
      else if (c === '\r') { /* ignore */ }
      else cur += c;
    }
  }
  if (cur.length > 0 || row.length > 0) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.length > 1 || (r.length === 1 && r[0].trim()));
};

const toApiRows = (csvRows) => {
  if (csvRows.length < 2) return { rows: [], headerMap: {}, unknown: [] };
  const headerRow = csvRows[0].map((h) => h.trim().toLowerCase());
  const headerMap = {};
  const unknown = [];
  headerRow.forEach((h, idx) => {
    if (!h) return;
    const apiKey = CSV_HEADERS_TO_API[h];
    if (apiKey) headerMap[idx] = apiKey;
    else unknown.push(h);
  });
  const apiRows = [];
  for (let r = 1; r < csvRows.length; r++) {
    const csvRow = csvRows[r];
    const obj = {};
    Object.entries(headerMap).forEach(([idx, apiKey]) => {
      const val = csvRow[Number(idx)];
      if (val !== undefined && val !== null && String(val).trim() !== '') obj[apiKey] = val;
    });
    if (Object.keys(obj).length > 0) apiRows.push(obj);
  }
  return { rows: apiRows, headerMap, unknown };
};

const downloadTemplate = (includeSamples = true) => {
  const lines = [TEMPLATE_HEADERS.join(',')];
  if (includeSamples) {
    SAMPLE_ROWS.forEach((row) => {
      lines.push(row.map((v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','));
    });
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bluespark_product_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const BulkUploadAdmin = () => {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState({ rows: [], headerMap: {}, unknown: [] });
  const [parseError, setParseError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createMissing, setCreateMissing] = useState(true);
  const [result, setResult] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const txt = ev.target.result;
      setCsvText(txt);
      try {
        const rows = parseCsv(txt);
        if (rows.length < 2) throw new Error('CSV must have a header row and at least one data row');
        const data = toApiRows(rows);
        setParsed(data);
        setParseError('');
      } catch (err) {
        setParseError(err.message);
        setParsed({ rows: [], headerMap: {}, unknown: [] });
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (parsed.rows.length === 0) {
      toast.error('No rows to upload');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await productsAPI.bulkUpsert(parsed.rows, createMissing);
      setResult(r);
      const note = `${r.updated} updated, ${r.created} created${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}`;
      toast.success(note);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bulk-upload-admin">
      <div>
        <h2 className="text-2xl font-bold">Bulk Product Upload</h2>
        <p className="text-sm text-gray-500">
          Update existing products and/or add new vehicles in one CSV upload.
          Uses the same column structure as your BlueSpark 2025 product Excel.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" /> 1. Get the template
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-gray-600">
            Download the CSV template with the correct headers. It includes 2 example rows: one that updates an
            existing SKU, and one that adds a brand-new vehicle (blank <code>sku</code>).
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadTemplate(true)} data-testid="download-template-with-samples">
              <Download className="mr-2 h-4 w-4" /> Template with examples
            </Button>
            <Button variant="ghost" onClick={() => downloadTemplate(false)} data-testid="download-template-blank">
              <Download className="mr-2 h-4 w-4" /> Blank template
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-5 w-5" /> 2. Upload your CSV
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            data-testid="csv-upload-input"
          />

          {parseError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{parseError}</span>
            </div>
          )}

          {parsed.unknown.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <div>
                <p className="font-semibold">Unknown columns ignored:</p>
                <p className="text-xs">{parsed.unknown.join(', ')}</p>
              </div>
            </div>
          )}

          {parsed.rows.length > 0 && (
            <>
              <p className="text-sm">
                Detected <span className="font-semibold">{parsed.rows.length}</span> row(s).
                Recognized fields:&nbsp;
                <span className="text-xs text-gray-500">
                  {Array.from(new Set(Object.values(parsed.headerMap))).join(', ')}
                </span>
              </p>
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Name / Title</th>
                      <th className="px-3 py-2">Make</th>
                      <th className="px-3 py-2">Model</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.sku || <span className="text-blue-600 font-medium">[new]</span>}
                        </td>
                        <td className="px-3 py-2 truncate max-w-sm">{r.name || '—'}</td>
                        <td className="px-3 py-2">{r.make || '—'}</td>
                        <td className="px-3 py-2">{r.model || '—'}</td>
                        <td className="px-3 py-2 text-right">{r.price || '—'}</td>
                        <td className="px-3 py-2 max-w-xs truncate text-xs">
                          {r.instructionsUrl ? '✓' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 5 && (
                  <p className="text-xs text-gray-500 p-2 text-center">…and {parsed.rows.length - 5} more rows</p>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={createMissing}
                  onChange={(e) => setCreateMissing(e.target.checked)}
                  data-testid="create-missing-checkbox"
                />
                <Plus className="h-4 w-4" />
                Create new products for SKUs not found / blank SKU rows
              </label>

              <Button onClick={handleSubmit} disabled={loading} data-testid="bulk-upload-submit" className="bg-blue-600 hover:bg-blue-700">
                <Upload className="mr-2 h-4 w-4" />
                {loading ? 'Uploading…' : `Upload ${parsed.rows.length} row(s)`}
              </Button>
            </>
          )}

          {result && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm space-y-1" data-testid="bulk-upload-result">
              <p className="font-semibold text-green-800">
                Done! {result.updated} product(s) updated, {result.created} product(s) created.
              </p>
              {result.skipped?.length > 0 && (
                <p className="text-amber-700 text-xs">
                  Skipped {result.skipped.length}: {result.skipped.slice(0, 5).map((s) => `${s.sku || '[no-sku]'} (${s.reason})`).join('; ')}
                  {result.skipped.length > 5 ? '…' : ''}
                </p>
              )}
              {result.errors?.length > 0 && (
                <p className="text-red-700 text-xs">
                  Errors on {result.errors.length} row(s): {result.errors.slice(0, 3).map((e) => `row ${e.row}: ${e.error}`).join('; ')}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 text-sm text-gray-700 space-y-2">
          <p className="font-semibold">CSV column reference (matches your Excel layout)</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 text-xs font-mono">
            <div>sku</div><div>product_title</div><div>product_url</div>
            <div>price</div><div>currency</div><div>manufacturer</div>
            <div>model</div><div>cc</div><div>stock_power</div>
            <div>tuned_power</div><div>stock_torque</div><div>tuned_torque</div>
            <div>fuel_system</div><div>connections</div><div>engine_id</div>
            <div>instructions_id</div><div>instructions_url</div><div>unbranded_instructions_url</div>
            <div>rail_map</div><div>boost</div><div>cam</div>
            <div>available</div><div>category</div><div>description</div>
            <div>image</div>
          </div>
          <ul className="mt-3 space-y-1 list-disc list-inside text-xs">
            <li>Leave <code>sku</code> blank to auto-generate one for a new vehicle.</li>
            <li>Existing SKUs are <strong>updated</strong>; only columns you provide change. Blank cells are ignored.</li>
            <li><code>tuned_power</code> / <code>tuned_torque</code> auto-compute the gain (tuned − stock).</li>
            <li><code>available</code> accepts: Yes, No, true, false, 1, 0.</li>
            <li><code>category</code> must be an existing category slug (pro-petrol, pro-diesel, pro-boost, 3-channel, petrol-sent, or one you've created).</li>
            <li>Empty cells and <code>#N/A</code> are skipped (DB value is preserved).</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkUploadAdmin;
